import { Header, Space } from '@procurify/ui'
import { type MessageDescriptor } from 'react-intl'

import { useProcurifyIntl } from '@webapp/hooks'
import { CatalogItemDetailsBlock } from './CatalogItemDetailsBlock'
import { CatalogItemDetailsSparkle } from './CatalogItemDetailsSparkle'
import messages from './messages'
import {
  type ConversationDetailBlock,
  type ConversationDetailSection,
  type ConversationDetailTextBlock,
  deriveDetailBlockKey,
} from '../../value-objects/ConversationTurnItem'
import {
  ConversationDetailBlockTypes,
  type ConversationDetailIconEnum,
  ConversationDetailIcons,
  ConversationDetailSectionIds,
} from '../../value-objects/enums'
import { CatalogItemTag } from '../CatalogItemTag'
import { CatalogItemDetailsAnalysisStyled } from './styles/CatalogItemDetailsAnalysisStyled'
import { CatalogItemDetailsSectionStyled } from './styles/CatalogItemDetailsSectionStyled'

export interface ICatalogItemDetailsSectionProps {
  section: ConversationDetailSection
  onChipSelect: (message: string) => void
}

const SECTION_FIXED_TITLES: Record<string, MessageDescriptor> = {
  [ConversationDetailSectionIds.AGENT_ANALYSIS]: messages.agentAnalysisTitle,
}

const SECTION_TITLE_FALLBACKS: Record<string, MessageDescriptor> = {
  [ConversationDetailSectionIds.SPECIFICATIONS]: messages.catalogDetailsTitle,
}

const SECTION_ICONS: Record<ConversationDetailIconEnum, () => JSX.Element> = {
  [ConversationDetailIcons.SPARKLE]: CatalogItemDetailsSparkle,
}

const isTextBlock = (
  block: ConversationDetailBlock
): block is ConversationDetailTextBlock =>
  block.type === ConversationDetailBlockTypes.TEXT

const SectionIcon = ({ icon }: { icon: ConversationDetailIconEnum }) => {
  const Glyph = SECTION_ICONS[icon]
  return Glyph ? <Glyph /> : null
}

export const CatalogItemDetailsSection = ({
  section,
  onChipSelect,
}: ICatalogItemDetailsSectionProps) => {
  const intl = useProcurifyIntl()

  const isCallout = section.variant === 'callout'
  const fixedTitle = SECTION_FIXED_TITLES[section.id]
  const titleFallback = SECTION_TITLE_FALLBACKS[section.id]
  const title = fixedTitle
    ? intl.formatMessage(fixedTitle)
    : (section.title ?? (titleFallback && intl.formatMessage(titleFallback)))

  // Callout sections deviate from render-in-array-order: text blocks are
  // hoisted into the tinted panel ahead of the remaining blocks.
  const calloutBodyBlocks = isCallout ? section.blocks.filter(isTextBlock) : []
  const remainingBlocks = isCallout
    ? section.blocks.filter((block) => !isTextBlock(block))
    : section.blocks

  return (
    <CatalogItemDetailsSectionStyled>
      <Space direction='vertical' size='medium'>
        {title && (
          <Space direction='horizontal' size='small' align='center'>
            {section.icon && <SectionIcon icon={section.icon} />}
            <Header as='h5'>{title}</Header>
          </Space>
        )}
        {section.tags && (
          <Space direction='horizontal' size='small' flexWrap>
            {section.tags.map((tag) => (
              <CatalogItemTag key={tag.text} {...tag} />
            ))}
          </Space>
        )}
        {calloutBodyBlocks.length > 0 && (
          <CatalogItemDetailsAnalysisStyled>
            <Space direction='vertical' size='medium'>
              {calloutBodyBlocks.map((block) => (
                <CatalogItemDetailsBlock
                  key={deriveDetailBlockKey(block)}
                  block={block}
                  isCallout
                  onChipSelect={onChipSelect}
                />
              ))}
            </Space>
          </CatalogItemDetailsAnalysisStyled>
        )}
        {remainingBlocks.map((block) => (
          <CatalogItemDetailsBlock
            key={deriveDetailBlockKey(block)}
            block={block}
            isCallout={false}
            onChipSelect={onChipSelect}
          />
        ))}
      </Space>
    </CatalogItemDetailsSectionStyled>
  )
}
